import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  Icon,
  Input,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Separator,
  Text,
  toastManager,
  useIsMobile,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import type { AddressSuggestion } from "#app/lib/hooks/address-search-types";
import { useAddressAutocomplete } from "#app/lib/hooks/use-address-autocomplete";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import { compositeShellProps } from "#app/lib/custom-field-utils";
import { at } from "#app/lib/utils/at";

const MOBILE_SNAP_POINTS = [0.75];

// Hover-revealed row actions are pure CSS, not a touch-device hook.
const HOVER_ACTIONS =
  "opacity-0 transition-opacity group-hover/address:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100";

// Autocomplete goes through useAddressAutocomplete → core's
// `addressAutocomplete` query (a server-side Google Places proxy), NOT the
// browser Maps JS SDK.
//
// An in-browser Places loader here injects a SECOND Maps bootstrap:
// @vis.gl/react-google-maps' APIProvider (map-view.tsx) mounts only with the
// map, so when the map and this input mount in the same commit — exactly what
// selecting an address in global search does, since it navigates to /map AND
// opens the create-project panel — two competing bootstraps make the Maps API
// throw from its own main.js and the map canvas never initializes: a black
// screen.
//
// The server proxy also keeps the Places key server-side and shares one code
// path with global search's address results, which already resolve this way.

type AddressPropertyInputProps = Omittable<{
  wholeAddress?: string | null;
  onSelect: (wholeAddress: string) => void;
  onClear?: () => void;
  /** Optional action — when provided, renders a map-pin icon button that jumps to the map screen and centers on this address. */
  onShowOnMap?: () => void;
  /** Visual treatment. Use "table" for grid/cell inputs without border radius;
   * "ghost" wraps the default trigger in the bordered transparent shell the
   * other custom-field renderers get on the ghost surface. */
  variant?: "default" | "table" | "ghost";
  /** Open the popover immediately when the control mounts into an active edit state. */
  autoOpen?: boolean;
  /** Called whenever the popover open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** When true, hides the autocomplete popover trigger and the clear
   * action — but keeps copy and map-pin hover actions available so
   * read-only viewers can still copy / locate. */
  readOnly?: boolean;
}>;

/** A hover-revealed row action. A real button element cannot nest inside the
 *  trigger button, so these are click targets on spans that stop propagation. */
function AddressAction({
  icon,
  label,
  onClick,
}: {
  icon: "map-pin" | "copy" | "x";
  label: string;
  onClick: () => void;
}) {
  return (
    <Box
      as="span"
      role="button"
      tabIndex={-1}
      aria-label={label}
      padding="xs"
      rounded="sm"
      cursor="pointer"
      className={HOVER_ACTIONS}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <Icon name={icon} size="md" color="muted" />
    </Box>
  );
}

export function AddressPropertyInput({
  wholeAddress,
  onSelect,
  onClear,
  onShowOnMap,
  variant = "default",
  autoOpen = false,
  onOpenChange,
  readOnly = false,
}: AddressPropertyInputProps) {
  const [open, setOpen] = useState(autoOpen);
  const [popoverSide, setPopoverSide] = useState<"top" | "bottom">("bottom");
  const popoverRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  const { suggestions } = useAddressAutocomplete(query);

  const clearState = useCallback(() => {
    setQuery("");
  }, []);

  const handleSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      onSelect(suggestion.description);
      clearState();
      setOpen(false);
    },
    [onSelect, clearState],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        if (!isMobile) {
          const el = popoverRef.current;
          if (el?.getBoundingClientRect) {
            const spaceBelow =
              window.innerHeight - el.getBoundingClientRect().bottom;
            setPopoverSide(spaceBelow >= 300 ? "bottom" : "top");
          }
        }
        clearState();
      }
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [clearState, isMobile, onOpenChange],
  );

  const handleCopy = useCallback(async () => {
    if (!wholeAddress) return;
    const ok = await copyToClipboard(wholeAddress);
    if (ok) {
      toastManager.add({ title: "Address copied", type: "success" });
    } else {
      toastManager.add({ title: "Failed to copy address", type: "error" });
    }
  }, [wholeAddress]);

  const hasValue = !!wholeAddress;

  const buttonTrigger = (
    <Button
      ref={popoverRef}
      variant="ghost"
      size="sm"
      // Sizes to its content like every sibling property selector; only the
      // table variant fills its cell. Button's base is `shrink-0`, and a
      // one-line address can outgrow a narrow panel — the shrink/min-w-0
      // override is what lets the Text truncate instead of widening the row.
      className={
        variant === "table"
          ? "group/address w-full justify-start"
          : "group/address max-w-full min-w-0 shrink justify-start"
      }
      onClick={!readOnly && isMobile ? () => handleOpenChange(true) : undefined}
    >
      <Box shrink minWidth={0}>
        <Text
          size="sm"
          weight="normal"
          variant={hasValue ? "default" : "muted"}
          truncate={1}
        >
          {wholeAddress || "—"}
        </Text>
      </Box>
      {wholeAddress && (
        <>
          <Box grow />
          {onShowOnMap && (
            <AddressAction
              icon="map-pin"
              label="Show on map"
              onClick={onShowOnMap}
            />
          )}
          <AddressAction icon="copy" label="Copy address" onClick={handleCopy} />
          {!readOnly && onClear && (
            <AddressAction icon="x" label="Clear address" onClick={onClear} />
          )}
        </>
      )}
    </Button>
  );

  const triggerButton =
    variant === "table" ? (
      <Box
        as="button"
        fill="flex"
        direction="row"
        align="center"
        paddingX="md"
        disabled={readOnly}
        onClick={readOnly ? undefined : () => handleOpenChange(true)}
      >
        <Box shrink>
          <Text
            size="sm"
            weight="normal"
            variant={hasValue ? "default" : "muted"}
            truncate={1}
          >
            {wholeAddress || "—"}
          </Text>
        </Box>
      </Box>
    ) : variant === "ghost" ? (
      // The same ghost shell sibling custom-field renderers get, so an
      // address field doesn't read borderless next to them.
      <Box {...compositeShellProps("ghost")} justify="center">
        {buttonTrigger}
      </Box>
    ) : (
      buttonTrigger
    );

  // Read-only mode skips the autocomplete popover/sheet entirely.
  // The trigger above gates its onClick + clear action on `readOnly`,
  // so what's left is a non-pressable display with hover-revealed
  // copy + map-pin actions.
  if (readOnly) return triggerButton;

  const searchInput = (
    <Box direction="row" align="center" gap="sm" padding="sm">
      <Icon name="search" size="lg" color="muted" />
      <Box fill="flex">
        {/* Submission is an Enter keydown on the input. */}
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search for address..."
          autoFocus
          unstyled
          onKeyDown={(event) => {
            if (event.key === "Enter" && suggestions.length > 0) {
              event.preventDefault();
              handleSelect(at(suggestions, 0, "address suggestion"));
            }
          }}
        />
      </Box>
    </Box>
  );

  const suggestionsList = suggestions.length > 0 ? (
    <>
      <Separator />
      <Box padding="sm" maxHeight={200}>
        {suggestions.map((s) => (
          <Box
            as="button"
            key={s.placeId}
            onClick={() => handleSelect(s)}
            paddingX="sm"
            paddingY="sm"
            rounded="sm"
            hoverBackground
            cursor="pointer"
          >
            <Text size="sm" truncate={1}>
              {s.description}
            </Text>
          </Box>
        ))}
      </Box>
    </>
  ) : null;

  const searchContent = (
    <>
      {searchInput}
      {suggestionsList}
    </>
  );

  const searchContentReversed = (
    <>
      {suggestionsList}
      {searchInput}
    </>
  );

  if (isMobile) {
    return (
      <Box fill={variant === "table" ? "flex" : undefined}>
        {triggerButton}
        <Drawer
          open={open}
          onOpenChange={handleOpenChange}
          snapPoints={MOBILE_SNAP_POINTS}
        >
          <DrawerPopup showBar>
            <DrawerHeader>
              <DrawerTitle>Search address</DrawerTitle>
            </DrawerHeader>
            <DrawerPanel className="px-0 pt-3">{searchContent}</DrawerPanel>
          </DrawerPopup>
        </Drawer>
      </Box>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger render={triggerButton} />
      {/* Width and the flush inset are class recipes — the popover viewport
          owns its padding and no prop reaches it. */}
      <PopoverPopup
        align="start"
        side={popoverSide}
        padding="none"
        className="w-80"
      >
        {popoverSide === "top" ? searchContentReversed : searchContent}
      </PopoverPopup>
    </Popover>
  );
}
